// "Simmering passion": a cracked ember with a heartbeat → the rage, words flickering in the fire → hold to feed the forge → the living fire turns to gold, still blazing → her painting emerges.
export const simmering = {
  slug: "simmering-passion",
  duration: 23.5,
  music: "the-descent",
  musicAt: 5.5,
  stages: [
    { id: "broken", at: 0, hint: "the broken heart" },
    { id: "rage", at: 6, hint: "the rage" },
    { id: "forge", at: 12, hint: "hold to feed the fire" },
    { id: "gold", at: 13.3, hint: "nothing turns to gold without the fire" },
  ],
  hold: { at: 12.5, need: 3, auto: 14 },
  touch: { kind: "press", from: 12, until: 13.1 },
  // the forge: a cracked heart-ember in a living fire, heat shimmering, gold beginning to flow
  layers: [
    { src: "/arcs/forge/forge.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, flicker: 0.12, glow: 0.3, opacity: [[0, 1], [12, 0.75], [13.3, 1]], show: [-1, 0, 99, 99] },
    // kintsugi: the heart's cracks fill with molten gold and the whole heart glows gold, while the flames keep blazing
    { src: "/arcs/forge/forge-gold.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.88, flicker: 0.12, glow: 0.35, shimmer: 0.25, show: [13.6, 17.5, 99, 99] },
    { src: "/arcs/ganges/flame.webp", add: true, flicker: 1, glow: 0.6, th: 0, y: 1.1, w: 1.3, h: 1.3, r: 4.3, show: [12, 13.5, 99, 99] },
  ],
  pulse: { from: 0.5, to: 12, every: 1.2 },
  words: { from: 6.5, to: 13, list: ["love", "hope", "change", "why", "love", "why"] },
  line: { text: "The fire and fury of broken experiences is necessary, for without the fire, nothing turns to gold.", from: 15.5, to: 31, pos: "low" },
  ambient: { scene: "ghat", until: 0, then: [] },
  look: { start: 0.12, lift: [0, 1], end: 0.12, yaw: [[0, 0], [6, 0.5], [10, -0.4], [12.5, 0], [23.5, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "simmer", pos: "low" },
  speed: [[0, 1], [23.5, 1]],
  reveal: { style: "flow", from: "center", duration: 10, ember: true },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uWall, uAspect, uTileH, uRadius, uCy, uSurface, uStill, uPulse;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float fbm(vec3 p){ float v = 0.0, a = 0.5; for (int i = 0; i < 3; i++){ v += a * snoise(p); p *= 2.03; a *= 0.5; } return v * 0.5 + 0.5; }
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
void main(){
  float w = uWall;
  float rage = smoothstep(5.5, 9.0, uT);
  float heat = uStill;
  float gk = smoothstep(13.5, 18.0, uT);
  vec3 col;
  vec2 local;
  vec2 P = uSurface < 0.5 ? vec2(atan(vPos.x, -vPos.z) * uRadius, vPos.y) : vPos.xz * 1.3 + vec2(0.0, 2.0);
  vec2 C = vec2(0.0, 2.2);
  // turbulent flames, warped and rising; they never cool
  vec2 warp = vec2(snoise(vec3(P * 0.45, w * 0.25)), snoise(vec3(P * 0.45 + 5.0, w * 0.25))) * 0.5 + 0.5;
  float v = fbm(vec3(P.x * 0.6 + warp.x * 1.6, P.y * 0.7 - w * (0.6 + 0.9 * rage) + warp.y * 1.4, w * 0.2));
  float core = exp(-length((P - C) * vec2(0.8, 1.0)) * (1.3 - 0.6 * heat)) * (0.25 + 0.9 * heat + 0.3 * rage);
  float f = clamp(v * (0.35 + 0.75 * rage) + core, 0.0, 1.4);
  vec3 fire = mix(vec3(0.12, 0.01, 0.03), vec3(0.72, 0.05, 0.05), smoothstep(0.2, 0.55, f));
  fire = mix(fire, vec3(1.0, 0.42, 0.06), smoothstep(0.55, 0.85, f));
  fire = mix(fire, vec3(1.0, 0.86, 0.38), smoothstep(0.85, 1.15, f));
  // the same moving flames, transmuting into shimmering fine gold
  vec3 gold = mix(vec3(0.3, 0.16, 0.03), vec3(0.86, 0.6, 0.18), smoothstep(0.25, 0.7, f));
  gold = mix(gold, vec3(1.0, 0.92, 0.6), smoothstep(0.75, 1.1, f));
  vec2 gc = floor(P * vec2(70.0, 22.0));
  float glit = step(0.965, hash2(gc + floor(w * 7.0))) * (1.0 - smoothstep(0.1, 0.5, abs(fract(P.x * 70.0) - 0.5)));
  gold += vec3(1.0, 0.95, 0.8) * glit * smoothstep(0.3, 0.6, f);
  float raised = gk * smoothstep(0.3 - 0.25 * gk, 0.6, f + 0.3 * gk);
  col = mix(fire, gold, raised);
  // the broken heart: a cracked ember, glowing with each beat
  float ed = length((P - C) * vec2(1.0, 1.15)) + 0.12 * snoise(vec3(P * 3.0, 2.0));
  float ember = 1.0 - smoothstep(0.48, 0.52, ed);
  float crack = 1.0 - smoothstep(0.02, 0.06, abs(snoise(vec3(P * 4.0, 7.0))));
  vec3 ec = mix(vec3(0.16, 0.03, 0.03), vec3(1.0, 0.5 + 0.3 * uPulse, 0.12), crack * (0.6 + 0.4 * uPulse));
  ec += vec3(0.9, 0.2, 0.05) * exp(-ed * 3.0) * uPulse * 0.6;
  col = mix(col, mix(ec, col, smoothstep(9.0, 13.0, uT)), ember * (uSurface < 0.5 ? 1.0 : 0.0));
  // white sparks flying as streaks from the forge
  float forge = smoothstep(12.0, 13.0, uT) + heat;
  for (int i = 0; i < 14; i++){
    float fi = float(i);
    float age = fract(w * (0.5 + 0.3 * hash1(fi)) + hash1(fi + 3.0));
    float a = (hash1(fi + 7.0) - 0.5) * 2.4 + 1.5708;
    vec2 vel = vec2(cos(a), sin(a)) * (2.0 + 2.0 * hash1(fi + 1.0));
    vec2 sp = C + vel * age - vec2(0.0, 1.2 * age * age);
    vec2 tail = sp - (vel - vec2(0.0, 2.4 * age)) * 0.06;
    float s = (1.0 - smoothstep(0.004, 0.012, seg(P, sp, tail))) * (1.0 - age);
    col = mix(col, mix(vec3(1.0), vec3(1.0, 0.85, 0.45), gk), s * clamp(forge, 0.0, 1.0) * (uSurface < 0.5 ? 1.0 : 0.0));
  }
  local = uSurface < 0.5 ? vec2(P.x, P.y - uCy) : vPos.xz;
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
